import { useEffect, useMemo, useState } from 'react';
import {
    collection,
    doc,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    writeBatch,
    deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

const FREE_LIMIT = 100;
const PREMIUM_LIMIT = 2000;

export const useAgenda = (agendaId = 'main') => {
    const { user, plan } = useContext(AuthContext);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!user) {
            setItems([]);
            return;
        }

        let colRef;
        if (agendaId === 'main') {
            colRef = collection(db, 'users', user.uid, 'agenda');
        } else {
            colRef = collection(db, 'users', user.uid, 'agendas', agendaId, 'contacts');
        }
        const q = query(colRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const data = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));
                setItems(data);
            },
            (err) => {
                setError(err);
            }
        );

        return () => unsubscribe();
    }, [user, agendaId]);

    const canAddMore = useMemo(() => {
        const limit = plan === 'premium' ? PREMIUM_LIMIT : FREE_LIMIT;
        return items.length < limit;
    }, [items.length, plan]);

    // Calcular filtros con conteos
    const filterOptions = useMemo(() => {
        const cityCounts = {};
        const categoryCounts = {};
        const statusCounts = {};

        items.forEach((item) => {
            if (item.city) {
                cityCounts[item.city] = (cityCounts[item.city] || 0) + 1;
            }
            if (item.category) {
                categoryCounts[item.category] = (categoryCounts[item.category] || 0) + 1;
            }
            if (item.status) {
                statusCounts[item.status] = (statusCounts[item.status] || 0) + 1;
            }
        });

        return {
            cities: Object.keys(cityCounts).sort().map(city => ({
                value: city,
                label: `${city} (${cityCounts[city]})`,
                count: cityCounts[city]
            })),
            categories: Object.keys(categoryCounts).sort().map(category => ({
                value: category,
                label: `${category} (${categoryCounts[category]})`,
                count: categoryCounts[category]
            })),
            statuses: Object.keys(statusCounts).sort().map(status => ({
                value: status,
                label: `${status} (${statusCounts[status]})`,
                count: statusCounts[status]
            }))
        };
    }, [items]);

    // Función helper para normalizar números de teléfono y detectar duplicados
    const normalizePhone = (phone) => {
        if (!phone) return '';
        // Eliminar todos los caracteres no numéricos
        return phone.replace(/\D/g, '');
    };

    const checkDuplicatePhone = (phone, excludePlaceId = null) => {
        if (!phone || phone.trim() === '' || phone === '-') return null;

        const normalizedPhone = normalizePhone(phone);
        if (normalizedPhone.length < 8) return null; // Números muy cortos probablemente inválidos

        const duplicate = items.find(item => {
            if (excludePlaceId && item.placeId === excludePlaceId) return false;
            const itemPhone = normalizePhone(item.phone);
            return itemPhone && itemPhone === normalizedPhone;
        });

        return duplicate;
    };

    const savePlaceToAgenda = async (place) => {
        if (!user) {
            const err = new Error('Debes iniciar sesión para guardar contactos.');
            err.code = 'AUTH_REQUIRED';
            throw err;
        }

        if (!canAddMore) {
            const limitMsg = plan === 'premium'
                ? `Has alcanzado el límite de ${PREMIUM_LIMIT} contactos del plan Premium.`
                : `Has alcanzado el límite de ${FREE_LIMIT} contactos del plan Free. Hazte Premium para guardar hasta ${PREMIUM_LIMIT} contactos.`;
            const err = new Error(limitMsg);
            err.code = 'LIMIT_REACHED';
            throw err;
        }

        // Aceptamos distintos posibles nombres de ID y generamos un fallback
        const finalPlaceId =
            place.placeId ||
            place.place_id ||
            place.id ||
            `${place.name || 'sin-nombre'}_${place.address || 'sin-direccion'}`;

        // Check if already exists by placeId
        if (items.some(item => item.placeId === finalPlaceId)) {
            const err = new Error('Este contacto ya está en tu agenda.');
            err.code = 'ALREADY_EXISTS';
            throw err;
        }

        // Check for duplicate phone number
        const duplicateByPhone = checkDuplicatePhone(place.phone);
        if (duplicateByPhone) {
            const err = new Error(`Ya existe un contacto con este número de teléfono: ${duplicateByPhone.name}`);
            err.code = 'DUPLICATE_PHONE';
            throw err;
        }

        setLoading(true);
        setError(null);
        try {
            let docRef;
            if (agendaId === 'main') {
                docRef = doc(db, 'users', user.uid, 'agenda', finalPlaceId);
            } else {
                docRef = doc(db, 'users', user.uid, 'agendas', agendaId, 'contacts', finalPlaceId);
            }
            const now = serverTimestamp();
            await setDoc(
                docRef,
                {
                    placeId: finalPlaceId,
                    name: place.name || '',
                    address: place.address || '',
                    phone: place.phone || '',
                    website: place.website || '',
                    email: place.email || '',
                    city: place.city || '',
                    category: place.category || '',
                    notes: place.notes || '',
                    status: place.status || 'pendiente',
                    createdAt: now,
                    updatedAt: now,
                },
                { merge: true }
            );
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const updateAgendaItem = async (placeId, partialData) => {
        if (!user || !placeId) return;
        let docRef;
        if (agendaId === 'main') {
            docRef = doc(db, 'users', user.uid, 'agenda', placeId);
        } else {
            docRef = doc(db, 'users', user.uid, 'agendas', agendaId, 'contacts', placeId);
        }
        try {
            await updateDoc(docRef, {
                ...partialData,
                updatedAt: serverTimestamp(),
            });
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    const deleteAgendaItem = async (placeId) => {
        if (!user || !placeId) return;
        let docRef;
        if (agendaId === 'main') {
            docRef = doc(db, 'users', user.uid, 'agenda', placeId);
        } else {
            docRef = doc(db, 'users', user.uid, 'agendas', agendaId, 'contacts', placeId);
        }
        try {
            await deleteDoc(docRef);
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    const saveManyPlacesToAgenda = async (places) => {
        if (!user) {
            const err = new Error('Debes iniciar sesión para guardar contactos.');
            err.code = 'AUTH_REQUIRED';
            throw err;
        }

        const existingIds = new Set(items.map((i) => i.placeId));
        const existingPhones = new Set(
            items.map(i => normalizePhone(i.phone)).filter(p => p && p.length >= 8)
        );
        // Normalizamos y filtramos los lugares que ya existen
        const normalized = places.map((p) => {
            const finalPlaceId =
                p.placeId ||
                p.place_id ||
                p.id ||
                `${p.name || 'sin-nombre'}_${p.address || 'sin-direccion'}`;
            return {
                ...p,
                placeId: finalPlaceId,
            };
        });
        // Filtrar por placeId duplicado Y por teléfono duplicado
        const newOnes = normalized.filter((p) => {
            // Ya existe por placeId
            if (existingIds.has(p.placeId)) return false;

            // Ya existe por teléfono
            const normalizedPhone = normalizePhone(p.phone);
            if (normalizedPhone && normalizedPhone.length >= 8 && existingPhones.has(normalizedPhone)) {
                return false;
            }

            return true;
        });
        if (newOnes.length === 0) {
            return { saved: 0, skipped: 0 };
        }

        let allowedCount = newOnes.length;
        const limit = plan === 'premium' ? PREMIUM_LIMIT : FREE_LIMIT;
        const remaining = limit - items.length;
        if (remaining <= 0) {
            const limitMsg = plan === 'premium'
                ? `Has alcanzado el límite de ${PREMIUM_LIMIT} contactos del plan Premium.`
                : `Has alcanzado el límite de ${FREE_LIMIT} contactos del plan Free.`;
            const err = new Error(limitMsg);
            err.code = 'LIMIT_REACHED';
            throw err;
        }
        allowedCount = Math.min(allowedCount, remaining);

        const toSave = newOnes.slice(0, allowedCount);

        // Filtrar duplicados dentro del mismo batch (por teléfono)
        const uniqueToSave = [];
        const seenPhones = new Set();

        for (const place of toSave) {
            const normalizedPhone = normalizePhone(place.phone);

            // Si no tiene teléfono o el teléfono ya fue visto en este batch, verificar
            if (normalizedPhone && normalizedPhone.length >= 8) {
                if (seenPhones.has(normalizedPhone)) {
                    continue;
                }
                seenPhones.add(normalizedPhone);
            }

            uniqueToSave.push(place);
        }

        const batch = writeBatch(db);
        const now = serverTimestamp();

        uniqueToSave.forEach((place) => {
            let ref;
            if (agendaId === 'main') {
                ref = doc(db, 'users', user.uid, 'agenda', place.placeId);
            } else {
                ref = doc(db, 'users', user.uid, 'agendas', agendaId, 'contacts', place.placeId);
            }
            batch.set(
                ref,
                {
                    placeId: place.placeId,
                    name: place.name || '',
                    address: place.address || '',
                    phone: place.phone || '',
                    website: place.website || '',
                    email: place.email || '',
                    city: place.city || '',
                    category: place.category || '',
                    notes: place.notes || '',
                    status: place.status || 'pendiente',
                    createdAt: now,
                    updatedAt: now,
                },
                { merge: true }
            );
        });

        await batch.commit();
        return { saved: uniqueToSave.length, skipped: newOnes.length - uniqueToSave.length };
    };

    const exportAgendaToCsv = () => {

        const headers = ['Nombre', 'Dirección', 'Teléfono', 'Website', 'Email', 'Notas', 'Estado'];
        const rows = items.map((item) => [
            `"${(item.name || '').replace(/"/g, '""')}"`,
            `"${(item.address || '').replace(/"/g, '""')}"`,
            `"${(item.phone || '').replace(/"/g, '""')}"`,
            `"${(item.website || '').replace(/"/g, '""')}"`,
            `"${(item.email || '').replace(/"/g, '""')}"`,
            `"${(item.notes || '').replace(/"/g, '""')}"`,
            `"${item.status || 'pendiente'}"`,
        ]);

        const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
        return csvContent;
    };

    const moveContactsToAgenda = async (placeIds, targetAgendaId) => {
        if (!user || !placeIds.length || !targetAgendaId) return;
        try {
            setLoading(true);
            const batch = writeBatch(db);

            // Para cada contacto, leer desde la agenda actual
            const contactsToMove = [];
            for (const placeId of placeIds) {
                const contact = items.find(item => item.placeId === placeId);
                if (contact) {
                    contactsToMove.push(contact);
                }
            }

            // Ahora copiar a la nueva agenda y eliminar de la actual
            contactsToMove.forEach((contact) => {
                // Crear en la agenda destino
                let targetDocRef;
                if (targetAgendaId === 'main') {
                    targetDocRef = doc(db, 'users', user.uid, 'agenda', contact.placeId);
                } else {
                    targetDocRef = doc(db, 'users', user.uid, 'agendas', targetAgendaId, 'contacts', contact.placeId);
                }

                // Copiar todos los datos del contacto excepto 'id'
                const { id, ...contactData } = contact;
                batch.set(targetDocRef, {
                    ...contactData,
                    updatedAt: serverTimestamp()
                });

                // Eliminar de la agenda actual
                let sourceDocRef;
                if (agendaId === 'main') {
                    sourceDocRef = doc(db, 'users', user.uid, 'agenda', contact.placeId);
                } else {
                    sourceDocRef = doc(db, 'users', user.uid, 'agendas', agendaId, 'contacts', contact.placeId);
                }
                batch.delete(sourceDocRef);
            });

            await batch.commit();
            setLoading(false);
        } catch (err) {
            setLoading(false);
            throw err;
        }
    };

    const deleteContacts = async (placeIds) => {
        if (!user || !placeIds.length) return;
        try {
            const batch = writeBatch(db);
            placeIds.forEach((placeId) => {
                let docRef;
                if (agendaId === 'main') {
                    docRef = doc(db, 'users', user.uid, 'agenda', placeId);
                } else {
                    docRef = doc(db, 'users', user.uid, 'agendas', agendaId, 'contacts', placeId);
                }
                batch.delete(docRef);
            });
            await batch.commit();
        } catch (err) {
            setError(err);
            throw err;
        }
    };

    const createManualContact = async (contactData, targetAgendaId = agendaId) => {
        if (!user) {
            const err = new Error('Debes iniciar sesión para guardar contactos.');
            err.code = 'AUTH_REQUIRED';
            throw err;
        }

        if (!canAddMore) {
            const limitMsg = plan === 'premium'
                ? `Has alcanzado el límite de ${PREMIUM_LIMIT} contactos del plan Premium.`
                : `Has alcanzado el límite de ${FREE_LIMIT} contactos del plan Free.`;
            const err = new Error(limitMsg);
            err.code = 'LIMIT_REACHED';
            throw err;
        }

        // Check for duplicate phone number
        const duplicateByPhone = checkDuplicatePhone(contactData.phone);
        if (duplicateByPhone) {
            const err = new Error(`Ya existe un contacto con este número de teléfono: ${duplicateByPhone.name}`);
            err.code = 'DUPLICATE_PHONE';
            throw err;
        }

        const finalPlaceId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        setLoading(true);
        setError(null);
        try {
            let docRef;
            if (targetAgendaId === 'main') {
                docRef = doc(db, 'users', user.uid, 'agenda', finalPlaceId);
            } else {
                docRef = doc(db, 'users', user.uid, 'agendas', targetAgendaId, 'contacts', finalPlaceId);
            }
            const now = serverTimestamp();
            await setDoc(
                docRef,
                {
                    placeId: finalPlaceId,
                    name: contactData.name || '',
                    address: contactData.address || '',
                    phone: contactData.phone || '',
                    website: contactData.website || '',
                    email: contactData.email || '',
                    city: contactData.city || '',
                    category: contactData.category || '',
                    notes: contactData.notes || '',
                    status: contactData.status || 'pendiente',
                    createdAt: now,
                    updatedAt: now,
                },
                { merge: true }
            );
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        items,
        loading,
        error,
        plan,
        savePlaceToAgenda,
        updateAgendaItem,
        exportAgendaToCsv,
        saveManyPlacesToAgenda,
        deleteAgendaItem,
        moveContactsToAgenda,
        deleteContacts,
        createManualContact,
        canAddMore,
        maxFree: FREE_LIMIT,
        maxPremium: PREMIUM_LIMIT,
        currentLimit: plan === 'premium' ? PREMIUM_LIMIT : FREE_LIMIT,
        filterOptions,
    };
};



